import styled from 'styled-components';
import { Card } from '@tw/tw-component-library';

export const PageContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  background-color: ${({ theme }) => theme.colors.background};
`;

export const ContentSectionContainer = styled.div<{ $isCompact?: boolean }>`
  max-width: ${({ $isCompact }) => ($isCompact ? '1180px' : 'none')};
  margin: 0 auto;
  padding: ${({ $isCompact }) => ($isCompact ? '2rem 2rem 2.5rem' : '2rem')};
`;

export const ContentSection = styled.div<{ $isCompact?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${({ $isCompact }) => ($isCompact ? '1rem' : '0')};
  background: ${({ $isCompact }) => ($isCompact ? 'transparent' : 'white')};
  border-radius: ${({ $isCompact }) => ($isCompact ? '0' : '8px')};
  box-shadow: ${({ $isCompact }) =>
    $isCompact ? 'none' : '0 1px 3px rgba(0, 0, 0, 0.1)'};
`;

export const WorkspaceOverviewHero = styled.section`
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  background:
    radial-gradient(
      circle at top right,
      ${({ theme }) => `${theme.colors.primary}16`} 0%,
      transparent 50%
    ),
    ${({ theme }) => theme.colors.background};
  box-shadow: 0 10px 26px rgba(15, 23, 42, 0.08);
  padding: 1.2rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

export const WorkspaceOverviewEyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  width: fit-content;
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 600;
  color: #155e75;
  background: #ecfeff;
  border: 1px solid #a5f3fc;
`;

export const WorkspaceOverviewTitle = styled.h2`
  margin: 0;
  font-size: 1.2rem;
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const WorkspaceOverviewSubtitle = styled.p`
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.45;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const SectionHeading = styled.h3`
  margin: 0 0 0.35rem;
  font-size: 0.95rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const SectionHint = styled.p`
  margin: 0 0 0.65rem;
  font-size: 0.82rem;
  line-height: 1.45;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const CompactSummaryCard = styled(Card)`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.85rem 0.95rem !important;
  border: 1px solid ${({ theme }) => theme.colors.border.light} !important;
  border-radius: ${({ theme }) => theme.borderRadius.sm} !important;
  box-shadow: none !important;
  transition: background-color 0.2s ease, border-color 0.2s ease;

  &:hover {
    transform: none !important;
    background-color: ${({ theme }) => `${theme.colors.primary}08`} !important;
    border-color: ${({ theme }) => `${theme.colors.primary}30`} !important;
  }

  h3 {
    margin: 0;
    font-size: 0.95rem;
    font-weight: 600;
  }
`;

export const CompactCardIcon = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border-radius: 999px;
  flex-shrink: 0;
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.primary};
  background: ${({ theme }) => `${theme.colors.primary}12`};
  border: 1px solid ${({ theme }) => `${theme.colors.primary}28`};
`;

export const CompactCardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  min-width: 0;
  flex: 1;
`;

export const CompactCardDescription = styled.p`
  margin: 0;
  font-size: 0.82rem;
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const CompactCardMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem 0.65rem;
  margin-top: 0.15rem;
  font-size: 0.78rem;
  color: ${({ theme }) => theme.colors.text.secondary};

  strong {
    font-weight: 600;
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

export const ContentSectionLists = styled.div<{ $isCompact?: boolean }>`
  display: ${({ $isCompact }) => ($isCompact ? 'grid' : 'flex')};
  grid-template-columns: ${({ $isCompact }) =>
    $isCompact ? 'repeat(2, minmax(0, 1fr))' : 'none'};
  flex-direction: ${({ $isCompact }) => ($isCompact ? 'unset' : 'row')};
  gap: ${({ $isCompact }) => ($isCompact ? '1rem' : '24px')};

  @media (max-width: 960px) {
    grid-template-columns: ${({ $isCompact }) => ($isCompact ? '1fr' : 'none')};
  }
`;

export const Title = styled.h1`
  margin: 0 0 16px 0;
  font-size: 28px;
  font-weight: 600;
  color: #1f2937;
`;

export const WorkspaceId = styled.p`
  margin: 0 0 24px 0;
  font-size: 14px;
  color: #6b7280;
  font-family: monospace;
`;

export const PlaceholderText = styled.p`
  color: #6b7280;
  font-size: 16px;
`;

export const CardGroup = styled.div<{ $isCompact?: boolean }>`
  display: grid;
  grid-template-columns: ${({ $isCompact }) =>
    $isCompact ? 'repeat(2, minmax(0, 1fr))' : 'repeat(4, 1fr)'};
  gap: ${({ $isCompact }) => ($isCompact ? '0.75rem' : '1rem')};

  @media (max-width: 1200px) {
    grid-template-columns: ${({ $isCompact }) =>
      $isCompact ? 'repeat(2, minmax(0, 1fr))' : 'repeat(4, 1fr)'};
  }

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

export const Icon = styled.div<{ $isCompact?: boolean }>`
  width: ${({ $isCompact }) => ($isCompact ? '2rem' : 'auto')};
  height: ${({ $isCompact }) => ($isCompact ? '2rem' : 'auto')};
  border-radius: ${({ $isCompact }) => ($isCompact ? '999px' : '0')};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: ${({ $isCompact }) => ($isCompact ? '1rem' : '24px')};
  margin-bottom: ${({ $isCompact }) => ($isCompact ? '0.5rem' : '8px')};
  color: ${({ theme, $isCompact }) =>
    $isCompact ? theme.colors.primary : '#007bff'};
  background: ${({ theme, $isCompact }) =>
    $isCompact ? `${theme.colors.primary}14` : 'transparent'};
  border: ${({ theme, $isCompact }) =>
    $isCompact ? `1px solid ${theme.colors.primary}33` : 'none'};
`;

export const Description = styled.p<{ $isCompact?: boolean }>`
  font-size: ${({ $isCompact }) => ($isCompact ? '0.85rem' : '14px')};
  margin: ${({ $isCompact }) => ($isCompact ? '0' : 'initial')};
  color: ${({ theme, $isCompact }) =>
    $isCompact ? theme.colors.text.secondary : '#6b7280'};
`;

export const Count = styled.p<{ $isCompact?: boolean }>`
  font-size: ${({ $isCompact }) => ($isCompact ? '0.85rem' : '14px')};
  margin: ${({ $isCompact }) => ($isCompact ? '0' : 'initial')};
  font-weight: ${({ $isCompact }) => ($isCompact ? '600' : '400')};
  color: ${({ theme, $isCompact }) =>
    $isCompact ? theme.colors.text.primary : '#6b7280'};
`;

export const Update = styled.p<{ $isCompact?: boolean }>`
  font-size: ${({ $isCompact }) => ($isCompact ? '0.8rem' : '14px')};
  margin: ${({ $isCompact }) => ($isCompact ? '0.2rem 0 0' : 'initial')};
  color: ${({ theme, $isCompact }) =>
    $isCompact ? theme.colors.text.secondary : '#6b7280'};
`;

export const ListsSectionCard = styled.section<{ $isCompact?: boolean }>`
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  border: ${({ theme, $isCompact }) =>
    $isCompact ? `1px solid ${theme.colors.border.light}` : 'none'};
  background: ${({ theme, $isCompact }) =>
    $isCompact ? theme.colors.background : 'transparent'};
  padding: ${({ $isCompact }) => ($isCompact ? '0.9rem' : '0')};
`;

export const CollapsibleSectionsStack = styled.div<{ $isCompact?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${({ $isCompact }) => ($isCompact ? '0.75rem' : '1rem')};
`;

export const CollapsibleSectionCard = styled.section`
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  background: ${({ theme }) => theme.colors.background};
  overflow: hidden;
`;

export const CollapsibleSectionHeader = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.85rem 1rem;
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: left;
  color: ${({ theme }) => theme.colors.text.primary};
  font: inherit;

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => `${theme.colors.primary}08`};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: -2px;
  }
`;

export const CollapsibleSectionHeaderText = styled.span`
  font-size: 0.95rem;
  font-weight: 600;
  line-height: 1.35;
`;

export const CollapsibleSectionToggle = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const CollapsibleSectionBody = styled.div`
  padding: 0 1rem 1rem;
  border-top: 1px solid ${({ theme }) => theme.colors.border.light};

  > p {
    margin: 0.75rem 0 0.65rem;
    font-size: 0.82rem;
    line-height: 1.45;
    color: ${({ theme }) => theme.colors.text.secondary};
  }
`;
